import { useMemo } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';

import { WEBEDI_CELL_HEIGHT } from 'support/helpers/const/const';

import { getSubColumnsPerParentColumnPath } from '../../utils/pathUtils';
import { EmbeddedTableRowRowSpan, RowWithEmbeddedTableRowInfo, WebEDIRowData } from '../../Types';
import {
  calculateCompressedRowHeight,
  calculateLeastCommonMultipleOfArrayLengths,
  createFieldInfo,
  getArrayLengthsPerParentPath,
} from './helpers';

/**
 * Expands data rows into embedded table rows for columns with array data at parent paths.
 *
 * When columns have a `parentColumnPath` pointing to array fields in the data,
 * this hook creates multiple embedded table rows per data row to display each array
 * item vertically. Regular columns use rowSpan to span all embedded table rows,
 * while embedded table row columns render one array item per embedded table row.
 *
 * Uses LCM (Least Common Multiple) to ensure integer rowSpan values, with
 * compressed row heights to avoid wasted vertical space.
 *
 * Example (with LCM and compression):
 * ```
 * Data: { name: "Product A", batches: [B1, B2], tags: [T1, T2, T3] }
 *
 * Arrays: batches=2, tags=3
 * LCM(2,3) = 6 rows total
 * Max items = 3
 * Compression: 6/3 = 2, so row height = 20px (instead of 40px)
 *
 * Renders as 6 rows × 20px = 120px total (same visual space as 3 normal rows):
 * Row 0: Product A (span=6) | B1 (span=3) | T1 (span=2)
 * Row 1: [spanned]          | [spanned]   | T2 (span=2)
 * Row 2: [spanned]          | [spanned]   | [spanned]
 * Row 3: [spanned]          | B2 (span=3) | T3 (span=2)
 * Row 4: [spanned]          | [spanned]   | [spanned]
 * Row 5: [spanned]          | [spanned]   | [spanned]
 * ```
 *
 * Each embedded table row contains an `embeddedTableRowRowSpans` object mapping parent paths to EmbeddedTableRowRowSpan,
 * which tells cells which array item index to render and the total count of items in that array (for rowSpan calculation).
 *
 * Flow:
 * 1. Parent column paths are received from the caller (computed once at DataTable level)
 * 2. Rows are expanded with embedded table row info
 * 3. Returns expanded rows consumed by TableRow.tsx for rowSpan logic
 * 4. BodyCell.tsx uses field info (itemIndex) to access the correct array item
 *
 * @param rows - Table rows from TanStack Table (may already be sliced by useVisibleRows)
 * @param columns - Column definitions to extract sub-column info from
 * @param parentColumnPaths - Pre-computed parent paths from DataTable level
 * @param enableEmbeddedRowActions - Whether to enable add and delete actions for embedded rows
 * @returns Array of embedded table row objects with field info for each array item
 */
export const useEmbeddedTableRows = <TData = WebEDIRowData, TValue = unknown>({
  rows,
  columns,
  parentColumnPaths,
  enableEmbeddedRowActions = false,
}: {
  rows: Array<Row<TData>>;
  columns: Array<ColumnDef<TData, TValue>>;
  parentColumnPaths: Array<string>;
  enableEmbeddedRowActions?: boolean;
}): Array<RowWithEmbeddedTableRowInfo<TData>> => {
  const subColumnsPerParentColumn = useMemo(
    () => getSubColumnsPerParentColumnPath(columns as Array<ColumnDef<TData, unknown>>),
    [columns],
  );
  const hasColumnGroups = parentColumnPaths?.length > 0;

  return useMemo(() => {
    if (!hasColumnGroups) {
      return rows.map((row) => ({
        row,
        embeddedTableRowIndex: 0,
        embeddedTableRowsCount: 1,
        isFirstEmbeddedTableRow: true,
        embeddedTableRowRowSpans: {},
        rowHeight: WEBEDI_CELL_HEIGHT,
      }));
    }

    return rows.flatMap((row) => {
      // Get lengths of all arrays: { items: 2, details: 3 }
      const arrayLengthsPerParentPath = getArrayLengthsPerParentPath(
        row.original,
        parentColumnPaths,
        enableEmbeddedRowActions,
      );

      const itemsCounts = Object.values(arrayLengthsPerParentPath);
      const maxItemsCount = Math.max(1, ...itemsCounts);

      // Calculate total rows using LCM to ensure integer rowSpan values
      const totalEmbeddedRowsCount = calculateLeastCommonMultipleOfArrayLengths(itemsCounts);

      // Compress row height to avoid wasted vertical space
      const rowHeight = calculateCompressedRowHeight(totalEmbeddedRowsCount, maxItemsCount);

      const embeddedTableRows = Array.from({ length: totalEmbeddedRowsCount }, (_, embeddedTableRowIndex) => {
        const embeddedTableRowRowSpans = parentColumnPaths.reduce<Record<string, EmbeddedTableRowRowSpan>>(
          (acc, path) => {
            acc[path] = createFieldInfo({
              itemsCount: arrayLengthsPerParentPath[path],
              embeddedTableRowIndex,
              embeddedTableRowsCount: totalEmbeddedRowsCount,
              addRowEnabled: enableEmbeddedRowActions,
              subColumnsCount: subColumnsPerParentColumn[path].length,
            });
            return acc;
          },
          {},
        );

        return {
          row,
          embeddedTableRowIndex,
          embeddedTableRowsCount: totalEmbeddedRowsCount,
          isFirstEmbeddedTableRow: embeddedTableRowIndex === 0,
          embeddedTableRowRowSpans,
          rowHeight,
        };
      });

      return embeddedTableRows;
    });
  }, [rows, parentColumnPaths, hasColumnGroups, enableEmbeddedRowActions, subColumnsPerParentColumn]);
};
